'use client';

import { useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Button, Typography } from '@/primitives';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EbvTraitExplanationPopover from './EbvTraitExplanationPopover';
import { EbvTraitHeaderLabel } from './EbvTraitHeaderLabel';
import {
  compareEbvByCategoryOrder,
  ebvGroupingOptionsFromDisplay,
  getEbvCategoryRanker,
  resolveEbvChartBarColour,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  ChartAxisBandAlign,
  EBV_CHART_AXIS_WIDTH_PX,
  type EbvChartItem,
  getCompactTraitLabel,
} from '@/components/publicSearch/animal/ebv/EbvPercentileGraph';
import { getGraphTraitName } from '@/components/publicSearch/animal/ebv/ebvPercentileUtils';
import type {
  EbvTraitHeaderContext,
  TraitExplanationEntry,
} from './ebvTraitExplanations';
import { useEbvChartLayout } from './useEbvChartLayout';
import { getGuideLabelTransform } from './guideLabel';
import EbvPotentialChangeInfoDialog from './EbvPotentialChangeInfoDialog';
import {
  ACCURACY_DOT_TIERS,
  calculateBarPosition,
  getAccuracyDotColor,
  getGuidePosition,
  getLeftDirection,
  getRangeValues,
  getUncertaintyHalfWidthPercent,
  parseAccuracyToProportion,
} from './ebvPotentialChangeUtils';

export interface EbvPotentialChangeGraphProps {
  data: EbvChartItem[];
  title?: string;
  /**
   * Center the title across the full card width instead of aligning it to the
   * chart's axis band. Use for long titles that would otherwise overflow.
   */
  centerTitle?: boolean;
  /** When set (EBV_AVERAGE `description` present), footer shows “Breed Avg” (same gate as percentile graph). */
  breedAverageMessage?: string;
  /** Trait explanations from EBV_DESCRIPTION (type 24), keyed by traitKey from EBV_FIELDS */
  traitExplanationsByKey?: Map<string, TraitExplanationEntry>;
  /** When false, hide BREEDPLAN tip sheet links (Custom / non-BP societies). */
  showBreedplanHelp?: boolean;
  /** Society value label from setting 26 (e.g. EBV, EPD) for empty-state copy. */
  ebvLabel?: string;
  /** Hides the Acc column and mobile accuracy overlay (e.g. mating pedigree EPA view). */
  hideAccuracyColumn?: boolean;
  /** Controlled open state for the “About” info dialog (used when the trigger is rendered outside this component). */
  infoOpen?: boolean;
  /** Called when the info dialog should close (used with `infoOpen`). */
  onInfoClose?: () => void;
  /** BlackRedAngus (CANG): group/sort/colour by COL chartColour slot. */
  useColourSlotGrouping?: boolean;
}

/** Public animal detail "EBV Potential Change Graph": SD / uncertainty band (i4-style). */
export default function EbvPotentialChangeGraph({
  data,
  title,
  centerTitle = false,
  breedAverageMessage,
  traitExplanationsByKey,
  showBreedplanHelp = true,
  ebvLabel = 'EBV',
  hideAccuracyColumn = false,
  infoOpen,
  onInfoClose,
  useColourSlotGrouping = false,
}: EbvPotentialChangeGraphProps) {
  const {
    theme,
    useCompactTraitLabels,
    percentileScale,
    traitColumnWidth,
    leftDirectionColumnWidth,
    rightDirectionColumnWidth,
    graphInfoOpen,
    setGraphInfoOpen,
    popoverAnchor,
    openPopover,
    popoverEntry,
    handleInfoClick,
    handlePopoverClose,
  } = useEbvChartLayout();

  const [showAccuracy, setShowAccuracy] = useState<boolean>(false);

  const effectiveInfoOpen = (infoOpen ?? false) || graphInfoOpen;
  const handleInfoClose = () => {
    setGraphInfoOpen(false);
    onInfoClose?.();
  };

  const traitHeaderContext = useMemo((): EbvTraitHeaderContext | undefined => {
    if (!traitExplanationsByKey) return undefined;
    return {
      traitExplanationsByKey,
      onTraitInfoClick: handleInfoClick,
    };
  }, [traitExplanationsByKey, handleInfoClick]);

  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );

  const chartData = useMemo(() => {
    const filtered = data.filter((item) => {
      if ((item.category || '') === 'Indexes') return false;

      const val = parseFloat(item.value);
      const se = parseFloat(item.standardError);
      const avg = parseFloat(item.breedAvg);
      const hasLowTop = item.lowValue !== '' && item.topValue !== '';

      return !isNaN(val) && !isNaN(se) && se > 0 && !isNaN(avg) && hasLowTop;
    });

    const rankCategory = getEbvCategoryRanker(filtered, ebvGroupingOptions);
    return filtered.sort((a, b) =>
      compareEbvByCategoryOrder(
        a.category,
        a.categoryOrder,
        b.category,
        b.categoryOrder,
        rankCategory,
      ),
    );
  }, [data, ebvGroupingOptions]);

  if (chartData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography
          variant="bodysmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          No {ebvLabel} potential change graph data available.
        </Typography>
      </Box>
    );
  }

  const footerPercentileCaption = breedAverageMessage?.trim() || undefined;
  const rightBandExtensionMd = hideAccuracyColumn ? 0 : 45;

  const xsGridCols = `${traitColumnWidth}px 40px 1fr 40px`;
  const mdGridCols = hideAccuracyColumn
    ? `${traitColumnWidth}px ${leftDirectionColumnWidth}px 50px ${EBV_CHART_AXIS_WIDTH_PX}px 50px ${rightDirectionColumnWidth}px`
    : `${traitColumnWidth}px ${leftDirectionColumnWidth}px 50px ${EBV_CHART_AXIS_WIDTH_PX}px 50px ${rightDirectionColumnWidth}px 45px`;
  const gridGapPx = parseFloat(theme.spacing(0.25)) || 2;
  const chartRowPadX = parseFloat(theme.spacing(1)) * 2;
  const mdColCount = hideAccuracyColumn ? 6 : 7;
  const chartBodyMinWidthMd =
    traitColumnWidth +
    leftDirectionColumnWidth +
    50 +
    EBV_CHART_AXIS_WIDTH_PX +
    50 +
    rightDirectionColumnWidth +
    (hideAccuracyColumn ? 0 : 45) +
    (mdColCount - 1) * gridGapPx +
    chartRowPadX;

  // Potential-change grid has 50px range columns each side of the axis; ChartAxisBandAlign must match (incl. gaps).
  const potentialBandAlignLeft = {
    xs: traitColumnWidth + 40 + 2 * gridGapPx,
    md: traitColumnWidth + leftDirectionColumnWidth + 50 + 3 * gridGapPx,
  };
  const potentialBandAlignRight = {
    xs: 40 + gridGapPx,
    md: hideAccuracyColumn
      ? 50 + rightDirectionColumnWidth + 2 * gridGapPx
      : 50 + rightDirectionColumnWidth + rightBandExtensionMd + 3 * gridGapPx,
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
        p: { xs: 1, md: 2.5 },
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {title &&
        (() => {
          const titleContent = (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: centerTitle ? 'center' : 'flex-start',
                gap: 0.5,
                minWidth: 0,
                ...(centerTitle && { textAlign: 'center' }),
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: 15,
                  letterSpacing: '-0.01em',
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              <Tooltip title="About this graph" enterDelay={300} arrow>
                <IconButton
                  size="small"
                  onClick={() => setGraphInfoOpen(true)}
                  aria-label="About the EBV Potential Change Graph"
                  sx={{
                    p: 0.25,
                    flexShrink: 0,
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );

          // Long titles (e.g. the mating predictor's) overflow the narrow
          // axis band, so center them across the full card width instead.
          if (centerTitle) {
            return (
              <Box
                sx={{
                  mb: { xs: 1, md: 2 },
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                {titleContent}
              </Box>
            );
          }

          return (
            <ChartAxisBandAlign
              traitColumnWidth={traitColumnWidth}
              leftDirectionColumnWidth={leftDirectionColumnWidth}
              rightDirectionColumnWidth={rightDirectionColumnWidth}
              rightExtensionMdPx={rightBandExtensionMd}
              trackWidthPx={
                useCompactTraitLabels ? undefined : EBV_CHART_AXIS_WIDTH_PX
              }
              leftBeforeTrackWidthPx={
                useCompactTraitLabels ? 0 : potentialBandAlignLeft
              }
              rightAfterTrackWidthPx={
                useCompactTraitLabels ? 0 : potentialBandAlignRight
              }
              sx={{ mb: { xs: 1, md: 2 }, display: { xs: 'none', md: 'flex' } }}
            >
              {titleContent}
            </ChartAxisBandAlign>
          );
        })()}
      <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
        <Box sx={{ minWidth: { xs: 0, md: chartBodyMinWidthMd } }}>
          {!hideAccuracyColumn && (
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: mdGridCols,
                alignItems: 'center',
                boxSizing: 'border-box',
                px: 1,
                mb: 0.5,
                gap: 0.25,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
              <Typography
                variant="bodysmall"
                sx={{
                  fontWeight: 600,
                  fontSize: 10,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: 'right',
                }}
              >
                Acc
              </Typography>
            </Box>
          )}

          {/* Scale row - mobile only, shown when accuracy visible */}
          {showAccuracy && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'flex-start',
                mb: 0.5,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{ width: traitColumnWidth + 40, flexShrink: 0, height: 16 }}
                aria-hidden
              />
              <Box sx={{ flex: 1, position: 'relative', height: 16 }}>
                {percentileScale.map((num) => (
                  <Typography
                    key={num}
                    variant="bodysmall"
                    sx={{
                      position: 'absolute',
                      left: getGuidePosition(num),
                      top: 0,
                      transform: getGuideLabelTransform(num),
                      fontSize: 10,
                      fontWeight: 500,
                      color: 'text.disabled',
                      fontFamily: 'monospace',
                      lineHeight: 1,
                    }}
                  >
                    {num}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ width: 40, flexShrink: 0, height: 16 }} aria-hidden />
            </Box>
          )}

          {/* Data rows */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {chartData.map((item) => {
              const color = resolveEbvChartBarColour(
                item.chartColour,
                item.category,
                item.colour,
                ebvGroupingOptions,
              );
              const traitName = getGraphTraitName(item);
              const leftDirection = getLeftDirection(item.leftLabel);

              const decimals = item.value.includes('.')
                ? item.value.split('.')[1]?.length || 1
                : 0;

              const rangeValues = getRangeValues(
                item.lowValue,
                item.topValue,
                item.breedAvg,
                item.standardError,
                decimals,
              );

              const barPosition = calculateBarPosition(
                item.value,
                item.breedAvg,
                item.lowValue,
                item.topValue,
                item.standardError,
              );

              const displayValue = item.value;

              // i4-style uncertainty band: width = 2(1 - r²) in SD units, centered on EBV value
              const halfWidthPercent = getUncertaintyHalfWidthPercent(
                item.accuracy,
              );
              const minBandWidthPercent = 4;
              const bandWidthPercent = Math.max(
                minBandWidthPercent,
                2 * halfWidthPercent,
              );
              const bandCenter = barPosition ?? 50;
              let barLeft = bandCenter - bandWidthPercent / 2;
              let barWidth = bandWidthPercent;
              barLeft = Math.max(0, Math.min(barLeft, 100 - barWidth));
              if (barLeft + barWidth > 100) barWidth = 100 - barLeft;

              // Anchor value label on the visible band so it stays centred when the band is clipped at 0% or 100%.
              const labelAnchorPercent =
                barPosition !== null ? barLeft + barWidth / 2 : 50;

              // Accuracy diamond x-position: map accuracy % directly to bar width (0 = left, 100 = right).
              const accuracyPercent =
                parseAccuracyToProportion(item.accuracy) * 100;

              const accuracyTooltip =
                !hideAccuracyColumn && item.accuracy
                  ? `${traitName}: Accuracy ${item.accuracy}`
                  : '';

              return (
                <Box
                  key={item.field}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: xsGridCols,
                      md: mdGridCols,
                    },
                    alignItems: 'center',
                    minHeight: 28,
                    gap: 0.25,
                    borderRadius: 1.5,
                    px: { xs: 0, md: 1 },
                    py: 0,
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {/* Trait name + optional info icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <Tooltip title={traitName} enterDelay={300} arrow>
                      <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                        <EbvTraitHeaderLabel
                          label={
                            useCompactTraitLabels
                              ? getCompactTraitLabel({
                                  ...item,
                                  label: traitName,
                                })
                              : traitName
                          }
                          traitKey={item.traitKey}
                          traitHeaderContext={traitHeaderContext}
                          align="left"
                          iconLayout="inline"
                          hideIconOnMobile
                          iconSize={14}
                          typographyProps={{
                            sx: {
                              fontSize: 11,
                              fontWeight: 600,
                              whiteSpace: useCompactTraitLabels
                                ? 'nowrap'
                                : { xs: 'normal', md: 'nowrap' },
                              overflow: useCompactTraitLabels
                                ? 'hidden'
                                : { xs: 'visible', md: 'hidden' },
                              textOverflow: useCompactTraitLabels
                                ? 'ellipsis'
                                : { xs: 'clip', md: 'ellipsis' },
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* Left direction - hidden on mobile */}
                  <Typography
                    variant="bodysmall"
                    sx={{
                      fontSize: 10,
                      textAlign: 'right',
                      color: 'text.disabled',
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    {leftDirection}
                  </Typography>

                  {/* Left range value */}
                  <Typography
                    variant="bodysmall"
                    sx={{
                      fontSize: 10,
                      textAlign: 'right',
                      color: 'text.secondary',
                      fontFamily: 'monospace',
                      pr: { xs: 0.25, md: 1 },
                    }}
                  >
                    {rangeValues.leftValue}
                  </Typography>

                  {/* Bar area */}
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: 32, md: 24 },
                      backgroundColor: 'background.paper',
                      backgroundImage: 'none',
                      border: 0,
                      borderRadius: 0,
                      position: 'relative',
                      isolation: 'isolate',
                      overflow: 'hidden',
                    }}
                  >
                    {percentileScale.map((guidePercentile) => {
                      return (
                        <Box
                          key={guidePercentile}
                          sx={{
                            position: 'absolute',
                            left: getGuidePosition(guidePercentile),
                            top: 0,
                            bottom: 0,
                            width: 0,
                            borderLeft: (theme) =>
                              `1px dashed ${alpha(theme.palette.text.secondary, 0.35)}`,
                            transform: getGuideLabelTransform(guidePercentile),
                            zIndex: 0,
                          }}
                        />
                      );
                    })}

                    {/* Accuracy diamond - mobile only, positioned at accuracy % along bar width */}
                    {showAccuracy && item.accuracy && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 3,
                          left: `${100 - accuracyPercent}%`,
                          transform: 'translateX(-50%) rotate(45deg)',
                          width: 6,
                          height: 6,
                          backgroundColor: getAccuracyDotColor(item.accuracy),
                          display: { xs: 'block', md: 'none' },
                          zIndex: 3,
                        }}
                      />
                    )}

                    {/* Colored bar */}
                    {barPosition !== null && (
                      <>
                        <Tooltip title={accuracyTooltip} enterDelay={200} arrow>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: { xs: 12, md: 3 },
                              bottom: 3,
                              left: `${barLeft}%`,
                              width: `${barWidth}%`,
                              backgroundColor: color,
                              borderRadius: 0.75,
                              zIndex: 1,
                              cursor: 'default',
                            }}
                          />
                        </Tooltip>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: { xs: 12, md: 3 },
                            bottom: 3,
                            left: `${labelAnchorPercent}%`,
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 24,
                            zIndex: 2,
                            // Let hover reach the band underneath so the accuracy tooltip still shows.
                            pointerEvents: 'none',
                          }}
                        >
                          <Typography
                            variant="bodysmall"
                            sx={{
                              display: 'block',
                              fontSize: 9,
                              fontWeight: 700,
                              color: 'text.primary',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {displayValue}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>

                  {/* Right range value */}
                  <Typography
                    variant="bodysmall"
                    sx={{
                      fontSize: 10,
                      textAlign: 'left',
                      color: 'text.secondary',
                      fontFamily: 'monospace',
                      pl: { xs: 0.25, md: 1 },
                    }}
                  >
                    {rangeValues.rightValue}
                  </Typography>

                  {/* Right direction - hidden on mobile */}
                  <Typography
                    variant="bodysmall"
                    sx={{
                      fontSize: 10,
                      color: 'text.disabled',
                      display: { xs: 'none', md: 'block' },
                    }}
                  >
                    {item.rightLabel}
                  </Typography>

                  {/* Accuracy - hidden on mobile */}
                  {!hideAccuracyColumn && (
                    <Typography
                      variant="bodysmall"
                      sx={{
                        fontSize: 10,
                        textAlign: 'right',
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: { xs: 'none', md: 'block' },
                      }}
                    >
                      {item.accuracy}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      {footerPercentileCaption && (
        <Typography
          variant="bodysmall"
          sx={{
            textAlign: 'center',
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 500,
            color: 'text.primary',
            pt: 1,
            px: 1,
            whiteSpace: 'normal',
            lineHeight: 1.35,
          }}
        >
          {footerPercentileCaption.trim()}
        </Typography>
      )}
      {/* Accuracy legend - mobile only, shown when accuracy visible */}
      {showAccuracy && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 1,
            pt: 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="bodysmall"
            sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}
          >
            Accuracy:
          </Typography>
          {ACCURACY_DOT_TIERS.map((tier) => (
            <Box
              key={tier.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  backgroundColor: tier.color,
                  flexShrink: 0,
                  transform: 'rotate(45deg)',
                }}
              />
              <Typography
                variant="bodysmall"
                sx={{
                  fontSize: 10,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {tier.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {/* Show/hide accuracy toggle - mobile only */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'center',
          pt: 1,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowAccuracy((prev) => !prev)}
          sx={{ fontSize: 11, py: 0.25, px: 1.5, textTransform: 'none' }}
        >
          {showAccuracy ? 'Hide Accuracy' : 'Show Accuracy'}
        </Button>
      </Box>
      <EbvPotentialChangeInfoDialog
        open={effectiveInfoOpen}
        onClose={handleInfoClose}
      />
      <EbvTraitExplanationPopover
        open={openPopover}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        entry={popoverEntry}
        showBreedplanHelp={showBreedplanHelp}
      />
    </Box>
  );
}
